import { useMemo, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { Button, Card } from '@repo/ui'
import {
  CERTIFICATE_FIELD_LABELS,
  CERTIFICATE_FIELD_LOCK_REASON,
  CERTIFICATE_HIDEABLE_FIELDS,
  CERTIFICATE_LOCKED_FIELDS,
  normalizeHiddenCertificateFields,
  type CertificateHideableField,
} from '@repo/types'
import { useProductCertificateFields } from 'hooks/api'
import { VisibilityToggle } from './fieldVisibilityControls'

interface CertificateFieldsCardProps {
  productId: string
  /** `product.hidden_certificate_fields` as the admin endpoint returns it. */
  initial: unknown
}

/**
 * VRG-788 — which STANDARD fields this SKU shows on its certificates.
 *
 * ## Why the locked rows are LISTED rather than omitted
 *
 * Product name, Certificate ID and Status render here, FIRST, locked, each with its
 * reason beside it. Leaving them out would be the obvious implementation and is worse in
 * a specific way: a brand looking for "stop showing the certificate ID" would find a list
 * that does not mention it and have no way to tell a deliberate rule from a missing
 * feature. §46's split is about an action's availability; this is the same reasoning
 * applied to a rule — a thing that cannot be changed should say so rather than be absent.
 *
 * ## ⚠️ The Vera verification mark is NOT a row here
 *
 * It was one until 2026-10-31. Removed because it is not a field — it is the page's own
 * chrome, nobody can turn it off, and a row in a list headed "which of these appear"
 * implies the answer could be no. The paragraph above applies to a rule a brand could
 * plausibly want changed; the mark is not that. Full reasoning in
 * `certificate-field-visibility.ts`.
 *
 * ## Why there is nothing here about the owner
 *
 * Owner identity, Owned since, Provenance and Activity are the OWNER's to show or hide
 * (`certificate_views.visibility`), and they are deliberately not represented on this
 * card even as locked rows. A locked row says "Vera decided this"; an owner-controlled
 * field is not Vera's decision, and listing it would invite the brand to ask for a
 * toggle over it — which is the precedence problem the three-actor split exists to
 * avoid. See `certificate-field-visibility.ts`.
 */
export const CertificateFieldsCard = ({ productId, initial }: CertificateFieldsCardProps) => {
  const { mutate: save, isPending } = useProductCertificateFields()

  // Normalized on the way IN as well as the way out: the column is jsonb, so a legacy
  // or hand-edited row can hold anything, and an unknown key would otherwise ride along
  // in the dirty check and be re-submitted (where the API would then 400 on it).
  const seed = useMemo(() => normalizeHiddenCertificateFields(initial), [initial])

  const [hidden, setHidden] = useState<CertificateHideableField[]>(seed)
  const [snapshot, setSnapshot] = useState(() => serialize(seed))

  const dirty = serialize(hidden) !== snapshot

  const toggle = (field: CertificateHideableField) =>
    setHidden((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    )

  const handleSave = () =>
    save({ productId, hiddenFields: hidden }, { onSuccess: () => setSnapshot(serialize(hidden)) })

  return (
    <Card title="Certificate visibility" info>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Which standard details appear on this product&apos;s certificates. Hiding a
          field removes it from the certificate only. It stays on the product, keeps
          importing, and is still shown here in the platform.
        </Typography>
      </Stack>

      <Stack>
        {/* LOCKED FIRST, then the brand's own. The order was the other way round and
            read as a list of settings with some disabled ones trailing it; what it
            actually is is a short statement of what the certificate always carries,
            followed by the part the brand decides. Putting the fixed part first means a
            reader meets the rule before the controls, so an inert row never arrives as a
            control that failed to work. It also pairs with the lock glyph: two groups,
            each with one icon, rather than seven rows of eye at two opacities. */}
        {[...CERTIFICATE_LOCKED_FIELDS, ...CERTIFICATE_HIDEABLE_FIELDS].map((field) => {
          const lockReason = (CERTIFICATE_FIELD_LOCK_REASON as Record<string, string>)[field]
          const isHidden = hidden.includes(field as CertificateHideableField)

          return (
            <Stack
              key={field}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                py: 1,
                // A hairline between rows, none under the last — the list ends where its
                // content ends rather than drawing a box around itself.
                '&:not(:last-of-type)': {
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              <Box>
                <Typography variant="body3">
                  {CERTIFICATE_FIELD_LABELS[field]}
                </Typography>
                {lockReason && (
                  <Typography
                    variant="body4"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.4, mt: '2px' }}
                  >
                    {/* The reason reads in the row as well as in the tooltip. Hover is
                        the wrong affordance for the explanation of a rule — a brand
                        scanning the list should not have to discover why the top three
                        rows are inert. */}
                    Always shown — {lockReason.charAt(0).toLowerCase() + lockReason.slice(1)}
                  </Typography>
                )}
              </Box>
              <VisibilityToggle
                visible={lockReason ? true : !isHidden}
                lockReason={lockReason}
                onToggle={() => toggle(field as CertificateHideableField)}
              />
            </Stack>
          )
        })}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="primary"
          disableElevation
          disabled={!dirty}
          loading={isPending}
          onClick={handleSave}
        >
          Save visibility
        </Button>
      </Stack>
    </Card>
  )
}

/**
 * Order-insensitive, because the stored list is a SET: toggling a field off and back on
 * puts it at the end of the array, and an order-sensitive snapshot would report that as
 * an unsaved change with nothing to save.
 */
function serialize(fields: CertificateHideableField[]): string {
  return [...fields].sort().join('|')
}
